import React, { useState, useEffect, useContext } from 'react';
import { Users, CheckCircle, XCircle, Clock, Mail, Building, Shield, Trash2, Edit2, UserPlus, X, RotateCcw, AlertTriangle } from 'lucide-react';
import { AuthContext } from './AuthContext';
import { fetchWithAuth, authenticatedFetch, API_ENDPOINT } from './utils/api';
import toast from 'react-hot-toast';

function RegistrationManagement() {
  const { organization } = useContext(AuthContext);
  const [existingUsers, setExistingUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [submitting, setSubmitting] = useState(false);

  // Invite states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    loadData();
  }, [organization]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (organization?.organizationId) {
        const usersData = await fetchWithAuth('/admin/users', organization.organizationId);
        setExistingUsers(usersData.users || []);

        try {
          const deletedData = await fetchWithAuth('/admin/users/deleted', organization.organizationId);
          setDeletedUsers(deletedData.deleted_users || []);
        } catch (error) {
          console.error('Error loading deleted users:', error);
          setDeletedUsers([]);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  // ── Guard helpers ─────────────────────────────────────────────────────────

  const adminCount = existingUsers.filter(u => u.role === 'admin').length;
  const totalUsers = existingUsers.length;

  // Last admin in the org
  const isLastAdmin = (user) =>
    user.role === 'admin' && adminCount === 1;

  // Last user in the org (regardless of role)
  const isLastUser = () => totalUsers === 1;

  // Whether a specific deleted user is the only admin (active + deleted combined)
  // Used to block permanent deletion of a deleted admin when no other admin exists
  const isLastAdminInDeleted = (deletedUser) => {
    const activeAdminCount = existingUsers.filter(u => u.role === 'admin').length;
    return deletedUser.role === 'admin' && activeAdminCount === 0;
  };

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleAddUser = async (e) => {
    e.preventDefault();

    if (!newUserEmail || !newUserName || !newUserRole) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    const loadingToast = toast.loading('Adding user...');

    try {
      await authenticatedFetch(
        `${API_ENDPOINT}/admin/users`,
        {
          method: 'POST',
          body: JSON.stringify({
            email: newUserEmail,
            name: newUserName,
            role: newUserRole,
            organization_id: organization.organizationId
          })
        }
      );

      toast.success('User added successfully!', { id: loadingToast });
      setShowAddUser(false);
      setNewUserEmail('');
      setNewUserName('');
      setNewUserRole('user');
      loadData();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Failed to add user', { id: loadingToast });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setInviting(true);
    const loadingToast = toast.loading('Sending invitation...');

    try {
      await authenticatedFetch(
        `${API_ENDPOINT}/admin/invite-user`,
        {
          method: 'POST',
          body: JSON.stringify({
            email: inviteEmail,
            name: inviteName,
            organization_id: organization.organizationId,
            organization_name: organization.organizationName || organization.organizationId,
            role: inviteRole,
            invited_by: organization.userEmail || 'admin'
          })
        }
      );

      toast.success(`Invitation sent to ${inviteEmail}`, { id: loadingToast });
      setInviteEmail('');
      setInviteName('');
      setInviteRole('member');
      setShowInviteModal(false);
      loadData();
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error(error.message || 'Failed to send invitation', { id: loadingToast });
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateUserRole = async (user, newRole) => {
    // Guard: prevent removing the last admin
    if (user.role === 'admin' && newRole !== 'admin' && adminCount === 1) {
      toast.error(
        'Cannot change role — at least one admin must remain in the organization.',
        { duration: 4000 }
      );
      return;
    }

    const loadingToast = toast.loading('Updating user role...');

    try {
      await authenticatedFetch(
        `${API_ENDPOINT}/admin/users/${user.user_id}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            role: newRole,
            organization_id: organization.organizationId
          })
        }
      );

      toast.success('User role updated', { id: loadingToast });
      loadData();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user role', { id: loadingToast });
    }
  };

  const handleRemoveUser = async (user) => {
    // Guard: cannot remove the only user in the org
    if (isLastUser()) {
      toast.error(
        'Cannot remove the only user in the organization. Invite another user first.',
        { duration: 4000 }
      );
      return;
    }

    // Guard: cannot remove the last admin
    if (isLastAdmin(user)) {
      toast.error(
        'Cannot remove the last admin. Assign another admin first.',
        { duration: 4000 }
      );
      return;
    }

    if (!window.confirm(`Remove ${user.email} from your organization? They can be restored within 30 days.`)) {
      return;
    }

    const loadingToast = toast.loading('Removing user...');

    try {
      await authenticatedFetch(
        `${API_ENDPOINT}/admin/users/${user.user_id}?organization_id=${organization.organizationId}`,
        { method: 'DELETE' }
      );

      toast.success('User removed successfully', { id: loadingToast });
      loadData();
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error('Failed to remove user', { id: loadingToast });
    }
  };

  const handleRestoreUser = async (userId, userEmail) => {
    if (!window.confirm(`Restore ${userEmail} to your organization?`)) {
      return;
    }

    const loadingToast = toast.loading('Restoring user...');

    try {
      await authenticatedFetch(
        `${API_ENDPOINT}/admin/users/${userId}/restore`,
        {
          method: 'POST',
          body: JSON.stringify({ organization_id: organization.organizationId })
        }
      );

      toast.success('User restored successfully', { id: loadingToast });
      loadData();
    } catch (error) {
      console.error('Error restoring user:', error);
      toast.error('Failed to restore user', { id: loadingToast });
    }
  };

  const handlePermanentDelete = async (user) => {
    // Guard: cannot permanently delete if this is the last admin
    // and there are no other active admins
    if (isLastAdminInDeleted(user)) {
      toast.error(
        'Cannot permanently delete — this is the only admin account. Restore them or assign another admin first.',
        { duration: 5000 }
      );
      return;
    }

    if (!window.confirm(`PERMANENTLY delete ${user.email}? This action cannot be undone!`)) {
      return;
    }

    const loadingToast = toast.loading('Permanently deleting user...');

    try {
      await authenticatedFetch(
        `${API_ENDPOINT}/admin/users/${user.user_id}/permanent`,
        {
          method: 'DELETE',
          body: JSON.stringify({ organization_id: organization.organizationId })
        }
      );

      toast.success('User permanently deleted', { id: loadingToast });
      loadData();
    } catch (error) {
      console.error('Error permanently deleting user:', error);
      toast.error('Failed to permanently delete user', { id: loadingToast });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading user management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">User Management</h2>
          <p className="text-sm text-gray-600 mt-1">Manage users and send invitations</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <UserPlus className="w-5 h-5" />
          <span>Invite User</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <Users className="w-5 h-5" />
            <span>Active Users</span>
            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">
              {existingUsers.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('deleted')}
            className={`${
              activeTab === 'deleted'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <Trash2 className="w-5 h-5" />
            <span>Recently Deleted</span>
            {deletedUsers.length > 0 && (
              <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full">
                {deletedUsers.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Active Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Active Users</h3>
                <p className="text-sm text-gray-500">{existingUsers.length} users in your organization</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddUser(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
            >
              <Users className="w-4 h-4" />
              <span>Add User</span>
            </button>
          </div>

          {/* Add User Modal */}
          {showAddUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">Add New User</h3>
                  <button onClick={() => setShowAddUser(false)} className="text-gray-400 hover:text-gray-600">
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleAddUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="user@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                    <select
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Admins can manage users and organization settings
                    </p>
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddUser(false);
                        setNewUserEmail('');
                        setNewUserName('');
                        setNewUserRole('user');
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                      disabled={submitting}
                    >
                      {submitting ? 'Adding...' : 'Add User'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {existingUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      No users found. Add users to get started.
                    </td>
                  </tr>
                ) : (
                  existingUsers.map((user) => {
                    const lastAdmin = isLastAdmin(user);
                    const lastUser  = isLastUser();
                    const blocked   = lastAdmin || lastUser;
                    const blockReason = lastUser
                      ? 'Only user — invite another user first'
                      : 'Last admin — assign another admin first';

                    return (
                      <tr key={user.user_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-semibold">
                                {user.email?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name || user.email}
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <select
                              value={user.role}
                              onChange={(e) => handleUpdateUserRole(user, e.target.value)}
                              disabled={lastAdmin || lastUser}
                              className={`text-sm border rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 ${
                                lastAdmin || lastUser
                                  ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'border-gray-300'
                              }`}
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                            {lastAdmin && !lastUser && (
                              <span
                                title="Last admin — cannot change role"
                                className="text-xs text-amber-600 font-medium flex items-center space-x-1"
                              >
                                <Shield className="w-3 h-3" />
                                <span>Last admin</span>
                              </span>
                            )}
                            {lastUser && (
                              <span
                                title="Only user in organization"
                                className="text-xs text-amber-600 font-medium flex items-center space-x-1"
                              >
                                <Shield className="w-3 h-3" />
                                <span>Only user</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleRemoveUser(user)}
                            disabled={blocked}
                            title={blocked ? blockReason : `Remove ${user.email}`}
                            className={`flex items-center space-x-1 ml-auto ${
                              blocked
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-red-600 hover:text-red-900'
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Remove</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recently Deleted Tab */}
      {activeTab === 'deleted' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Trash2 className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Recently Deleted Users</h3>
                <p className="text-sm text-gray-500">Users deleted in the past 30 days can be restored</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {deletedUsers.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No recently deleted users</p>
                <p className="text-sm text-gray-500 mt-1">Deleted users will appear here for 30 days</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deletedUsers.map((user) => {
                  const blockedPermanent = isLastAdminInDeleted(user);
                  return (
                    <div key={user.user_id} className="border border-red-200 rounded-lg p-4 bg-red-50 hover:bg-red-100 transition">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-400 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold">
                              {user.email?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.name || user.email}</div>
                            <div className="text-sm text-gray-600">{user.email}</div>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-xs text-gray-500">Role: {user.role}</span>
                              <span className="text-xs text-red-600">
                                Deleted: {new Date(user.deleted_at).toLocaleDateString()}
                              </span>
                              <span className="text-xs text-gray-500">
                                Expires: {new Date(new Date(user.deleted_at).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                              </span>
                            </div>
                            {blockedPermanent && (
                              <p className="text-xs text-amber-600 mt-1 flex items-center space-x-1">
                                <Shield className="w-3 h-3" />
                                <span>Only admin — restore or assign another admin before permanently deleting</span>
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleRestoreUser(user.user_id, user.email)}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2"
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span>Restore</span>
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(user)}
                            disabled={blockedPermanent}
                            title={blockedPermanent ? 'Cannot permanently delete the only admin' : 'Permanently delete'}
                            className={`px-3 py-2 rounded-lg transition flex items-center space-x-2 ${
                              blockedPermanent
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            <AlertTriangle className="w-4 h-4" />
                            <span>Delete Forever</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Invite New User</h3>
              <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={inviting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name (Optional)</label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={inviting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={inviting}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                <p className="text-sm text-blue-800">
                  <strong>📧 Email Invitation</strong><br />
                  The user will receive an email with a secure link to set up their password and access the platform immediately.
                </p>
              </div>
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvite}
                  disabled={inviting || !inviteEmail}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default RegistrationManagement;